import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera } from "lucide-react";
import Navbar from "../components/lac/Navbar";
import Footer from "../components/lac/Footer";
import { api } from "../lib/api";

function MediaCard({ item }) {
  // Embed TikTok/Instagram/YouTube where possible, otherwise show as a card with link.
  const url = item.media_url;
  const isImage = /\.(jpe?g|png|webp|gif|heic)(?:\?.*)?$/i.test(url);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`gallery-item-${item.id}`}
      className="group block overflow-hidden rounded-sm border border-white/10 bg-[#0d0d0d] hover:border-[#D946EF]/60 hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-[4/5] bg-black">
        {isImage ? (
          <img src={url} alt={`${item.name}'s LAC install`} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500">
            <Camera className="w-8 h-8 mb-2" />
            <div className="text-xs tracking-[0.25em]">TAP TO WATCH</div>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="font-display font-semibold text-sm text-white truncate">{item.name}</div>
        {item.vehicle && <div className="text-xs text-zinc-500 mt-0.5 truncate">{item.vehicle}</div>}
        {item.note && <p className="text-xs text-zinc-400 mt-2 leading-relaxed line-clamp-3">{item.note}</p>}
      </div>
    </a>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/gallery")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-32 pb-24" data-testid="gallery-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition" data-testid="gallery-back">
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-3">CUSTOMER GALLERY</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
              Built by LAC.<br />
              <span className="neon-violet">Filmed by customers.</span>
            </h1>
            <p className="text-zinc-400 mt-4 font-body leading-relaxed max-w-xl">
              Real cars, real customers. Every clip and photo below was sent in by someone who
              came through the workshop. Want yours up here? Send it in.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/gallery/submit"
              data-testid="gallery-submit-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D946EF] text-white font-semibold text-sm hover:bg-[#F0ABFC] transition"
            >
              Submit yours <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 rounded-sm border border-white/10 bg-[#0a0a0a] text-center" data-testid="gallery-empty">
            <Camera className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">Gallery's just opened.</h3>
            <p className="text-zinc-400 text-sm mb-6">Be the first customer to send us your install — we'll feature you right here.</p>
            <Link
              to="/gallery/submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D946EF] text-white font-semibold text-sm hover:bg-[#F0ABFC] transition"
            >
              Send my POV <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {items.map((it) => <MediaCard key={it.id} item={it} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
